import { useState, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from 'app/store/store.hooks';
import { setRewardsPopupResolved } from 'app/store/reducers/ui.reducer';

export const SESSION_FRESH_LOGIN_KEY = 'fresh_login';
const LS_HIDDEN_UNTIL_KEY = 'bitecoins_popup_hidden_until';
const SESSION_POPUP_DONE_KEY = 'bitecoins_popup_done';
const LS_REFERRAL_REWARD_SHOWN_KEY = 'referral_reward_popup_shown';
const HIDE_DURATION_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

export function markFreshLogin() {
  sessionStorage.setItem(SESSION_FRESH_LOGIN_KEY, 'true');
}

export function markLogout() {
  sessionStorage.removeItem(SESSION_FRESH_LOGIN_KEY);
  sessionStorage.removeItem(SESSION_POPUP_DONE_KEY);
}

/**
 * Guard for ReferralRewardPopup: the BE `hasUnseenReferralReward` flag is the source of
 * truth, but its reset (POST /customers/me/referral/seen) is fired the moment the popup is shown —
 * an immediate F5 (or tab close) can kill that request in flight and resurrect the popup on the
 * next load. The mark is written synchronously at show time into localStorage (survives reloads
 * AND tab close/reopen), keyed by userId + amount so NEW accumulated rewards still pop, and
 * cleared on logout/clearAuth so a fresh login is never suppressed by a previous account/session.
 */
export function markReferralRewardPopupShown(userId: string, amount: number) {
  try {
    localStorage.setItem(LS_REFERRAL_REWARD_SHOWN_KEY, `${userId}:${amount}`);
  } catch {
    /* noop: storage unavailable — BE seen-flag still guards */
  }
}

export function isReferralRewardPopupShown(userId: string, amount: number): boolean {
  try {
    return localStorage.getItem(LS_REFERRAL_REWARD_SHOWN_KEY) === `${userId}:${amount}`;
  } catch {
    return false;
  }
}

export function clearReferralRewardPopupMark() {
  try {
    localStorage.removeItem(LS_REFERRAL_REWARD_SHOWN_KEY);
  } catch {
    /* noop */
  }
}

export function useRewardsPopup() {
  const dispatch = useAppDispatch();
  const [isVisible, setIsVisible] = useState(false);
  const { authModelLoading } = useAppSelector((state) => state.auth);
  const { customerModel, customerModelLoading } = useAppSelector((state) => state.customer);
  const isAuthorized = !authModelLoading && !customerModelLoading && !!customerModel;

  useEffect(() => {
    if (!isAuthorized) {
      setIsVisible(false);
      return;
    }

    const isFreshLogin = sessionStorage.getItem(SESSION_FRESH_LOGIN_KEY) === 'true';
    if (!isFreshLogin) {
      setIsVisible(false);
      dispatch(setRewardsPopupResolved(true));

      return;
    }

    const isPopupDoneThisSession = sessionStorage.getItem(SESSION_POPUP_DONE_KEY) === 'true';
    if (isPopupDoneThisSession) {
      setIsVisible(false);
      dispatch(setRewardsPopupResolved(true));

      return;
    }

    const hiddenUntil = localStorage.getItem(LS_HIDDEN_UNTIL_KEY);
    if (hiddenUntil && Date.now() < Number(hiddenUntil)) {
      setIsVisible(false);
      dispatch(setRewardsPopupResolved(true));

      return;
    }

    setIsVisible(true);
    // Explicitly re-block: guards against a stale `true` left over from a previous account's
    // session (logout → login in the same tab, no reload) wrongly unblocking ReferralRewardPopup.
    dispatch(setRewardsPopupResolved(false));
  }, [isAuthorized, dispatch]);

  const closePopup = (dontShowAgain: boolean) => {
    if (dontShowAgain) {
      localStorage.setItem(LS_HIDDEN_UNTIL_KEY, String(Date.now() + HIDE_DURATION_MS));
    }

    sessionStorage.setItem(SESSION_POPUP_DONE_KEY, 'true');
    setIsVisible(false);
    dispatch(setRewardsPopupResolved(true));
  };

  return { isVisible, closePopup };
}
